import { z } from 'zod';
import { getItemById } from '../items/items';
import { getSkillById } from '../skills/skills';
import { roomOptionSchema } from '../floors/floorSchema';
import { activeStatusSchema, characterStatsSchema, factionSchema } from '../characters/characterSchemas';
import dungeonMerchantJson from './data/dungeon_merchant.json';
import wanderingSageJson from './data/wandering_sage.json';

export const npcSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  title: z.string().min(1),
  description: z.string().min(1),
  faction: factionSchema,
  stats: characterStatsSchema,
  skillIds: z.array(z.string().min(1)).default([]),
  statuses: z.array(activeStatusSchema).default([]),
  interactions: z.array(roomOptionSchema).min(1),
});

export type NpcDef = z.infer<typeof npcSchema>;

export const npcs: NpcDef[] = z.array(npcSchema).parse([
  dungeonMerchantJson,
  wanderingSageJson,
]);

for (const npc of npcs) {
  for (const skillId of npc.skillIds) {
    const skill = getSkillById(skillId);
    if (!skill) {
      throw new Error(`NPC ${npc.id} references unknown skill ${skillId}`);
    }
    if (skill.faction !== npc.faction) {
      throw new Error(`NPC ${npc.id} cannot use ${skillId} from faction ${skill.faction}`);
    }
  }
  for (const option of npc.interactions) {
    const { learnSkillId, grantItemId } = option.consequence;
    if (learnSkillId && !getSkillById(learnSkillId)) {
      throw new Error(`NPC ${npc.id} teaches unknown skill ${learnSkillId}`);
    }
    if (grantItemId && !getItemById(grantItemId)) {
      throw new Error(`NPC ${npc.id} grants unknown item ${grantItemId}`);
    }
  }
}

export function getNpcById(id: string): NpcDef | undefined {
  return npcs.find((npc) => npc.id === id);
}

export function pickNpc(rng: () => number): NpcDef {
  return npcs[Math.floor(rng() * npcs.length)] ?? npcs[0] as NpcDef;
}
